import { KeyboardEvent } from 'react';
import { Typography } from '@qodo/design-system';
import { KEYBOARD_KEYS } from 'consts/keyboard';
import { cn } from 'utils/className';
import { useMultiStepDialog } from '../multiStepDialogContext';
import { StepConfig } from '../types';
import { canNavigateToStep } from '../utils';

export const Sidebar = () => {
  const {
    completedSteps,
    currentStepIndex,
    disabledSteps,
    earliestStepAvailable,
    furthestStepReached,
    goToIndex,
    hiddenSteps,
    navigationMode,
    steps,
  } = useMultiStepDialog();

  // Preserve each step's original index (navigation/active/completed logic keys
  // off it) while dropping hidden steps from the rendered list.
  const visibleSteps = steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => !hiddenSteps.has(step.id));
  const isStepActive = (index: number) => index === currentStepIndex;

  const isStepAvailable = (index: number) =>
    !disabledSteps.has(steps[index]?.id ?? '') &&
    canNavigateToStep(
      index,
      steps,
      navigationMode,
      furthestStepReached,
      earliestStepAvailable,
    );

  const isStepCompleted = (index: number) =>
    completedSteps.has(index) && index < currentStepIndex;

  const onClick = (index: number) => {
    if (!isStepAvailable(index)) return;

    goToIndex(index);
  };

  const onKeyDown = (
    e: KeyboardEvent<HTMLElement>,
    _step: StepConfig,
    index: number,
  ) => {
    if (
      (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.SPACE) &&
      isStepAvailable(index)
    ) {
      e.preventDefault();
      onClick(index);
    }
  };

  const renderStep = (step: StepConfig, index: number) => {
    const active = isStepActive(index);
    const completed = isStepCompleted(index);
    const isDisabled = !isStepAvailable(index);

    const stepClassName = cn('mt-0! py-1.5 text-sm', {
      'cursor-not-allowed': isDisabled,
      'text-text-action-emphasis-idle cursor-default': active,
      'text-text-general-neutral-mild': completed && !active,
      'cursor-pointer': completed && !active && !isDisabled,
      'text-text-action-neutral-idle cursor-pointer':
        !active && !completed && !isDisabled,
    });

    const isExplicitlyDisabled = disabledSteps.has(step.id);
    const disabledStyle =
      isExplicitlyDisabled && step.disabledColor
        ? { color: step.disabledColor }
        : undefined;

    return (
      <li
        key={step.id}
        data-step-id={step.id}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-current={active ? 'step' : undefined}
        className={stepClassName}
        style={disabledStyle}
        onClick={() => onClick(index)}
        onKeyDown={(e) => onKeyDown(e, step, index)}
      >
        {step.title}
      </li>
    );
  };

  return (
    <nav className="min-w-61 pl-8 pr-10 pt-10 pb-12">
      <Typography
        variant="list-ordered"
        as="ol"
        className="flex flex-col gap-3 m-0 ml-5!"
      >
        {visibleSteps.map(({ step, index }) => renderStep(step, index))}
      </Typography>
    </nav>
  );
};
